import React, { useEffect, useState, useCallback } from 'react';
import {
  Input,
  Select,
  Button,
  Spin,
  Empty,
  Avatar,
  Badge,
  Typography,
  Result,
  Alert
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  RobotOutlined,
  MessageOutlined,
  BellOutlined
} from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { getUserInterfacesFromStorage, hasInterfacePermission } from '../utils/permissionUtils';
import useChatBotConversation from '../components/ChatBotConversation/useChatBotConversation';
import ChatSessionList from '../components/ChatBotConversation/ChatSessionList';
import ChatConversationPanel from '../components/ChatBotConversation/ChatConversationPanel';
import { getInitial, formatDateTime } from '../components/ChatBotConversation/utils';

const { Text, Title } = Typography;
const { Option } = Select;

const POLL_INTERVAL_MS = 30000; // 30 秒後台輪詢

const ChatBotInboxPage = () => {
  const { t } = useLanguage();

  // 篩選狀態
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [source, setSource] = useState('all');
  const [contactType, setContactType] = useState('all');

  // 權限
  const [userInterfaces, setUserInterfaces] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoadingPermissions(true);
      try {
        const ifaces = await getUserInterfacesFromStorage(true);
        if (isMounted) setUserInterfaces(ifaces || []);
      } catch {
        if (isMounted) setUserInterfaces([]);
      } finally {
        if (isMounted) setLoadingPermissions(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);

  const canAccess =
    !loadingPermissions && hasInterfacePermission(userInterfaces, 'chatBotInbox');

  const {
    loading,
    sessions,
    activeSessionId,
    setActiveSessionId,
    activeSession,
    combinedMessages,
    interventionMessages,
    inputText,
    setInputText,
    sending,
    loadSessions,
    handleSendIntervention,
    unreadCounts,
    totalUnread,
    newMessagesForActive,
    newSessionIds
  } = useChatBotConversation({
    mode: 'inbox',
    inboxParams: { source, contactType, search },
    enabled: canAccess,
    pollIntervalMs: POLL_INTERVAL_MS,
    loadFailedFallback: t('chatBotInbox.loadFailed') || '載入對話列表失敗'
  });

  useEffect(() => {
    if (canAccess) loadSessions();
  }, [canAccess, source, contactType, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = useCallback(() => {
    setSearch(searchInput.trim());
  }, [searchInput]);

  const getExtraInfo = useCallback((session) => {
    const parts = [];
    if (session.workflowName) parts.push(session.workflowName);
    if (session.taskName) parts.push(session.taskName);
    if (session.source === 'chatOrderingCart') parts.push('購物車');
    return parts.join(' · ') || undefined;
  }, []);

  if (loadingPermissions) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!canAccess) {
    return (
      <Result
        status="403"
        title="403"
        subTitle={t('common.noPermission') || '您沒有權限訪問此頁面'}
      />
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 頁面標題列 */}
      <div
        style={{
          padding: '12px 20px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fff',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}
      >
        <MessageOutlined style={{ fontSize: 18, color: '#1677ff' }} />
        <Title level={5} style={{ margin: 0 }}>
          {t('menu.chatBotInbox') || 'ChatBot 對話'}
        </Title>
        {/* 總未讀 badge */}
        {totalUnread > 0 && (
          <Badge
            count={totalUnread}
            style={{ backgroundColor: '#ff4d4f' }}
            title={`${totalUnread} 則未讀訊息`}
          />
        )}
      </div>

      {/* 主體佈局：左欄列表 + 右欄對話 */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
        {/* 左欄 */}
        <div
          style={{
            width: 320,
            borderRight: '1px solid #f0f0f0',
            display: 'flex',
            flexDirection: 'column',
            background: '#fafafa',
            flexShrink: 0
          }}
        >
          {/* 篩選區 */}
          <div
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid #f0f0f0',
              background: '#fff',
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}
          >
            <Input
              prefix={<SearchOutlined style={{ color: '#bbb' }} />}
              placeholder={t('chatBotInbox.searchPlaceholder') || '搜尋聯絡人或號碼…'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
              onClear={() => { setSearchInput(''); setSearch(''); }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Select
                size="small"
                value={contactType}
                onChange={setContactType}
                style={{ flex: 1 }}
              >
                <Option value="all">{t('chatBotInbox.filterAll') || '全部'}</Option>
                <Option value="contact">{t('chatBotInbox.filterContact') || '聯絡人'}</Option>
                <Option value="anonymous">{t('chatBotInbox.filterAnonymous') || '匿名'}</Option>
              </Select>
              <Select
                size="small"
                value={source}
                onChange={setSource}
                style={{ flex: 1 }}
              >
                <Option value="all">{t('chatBotInbox.sourceAll') || '全部來源'}</Option>
                <Option value="chatBot">{t('chatBotInbox.sourceChatBot') || 'ChatBot'}</Option>
                <Option value="chatOrderingCart">
                  {t('chatBotInbox.sourceChatOrderingCart') || '購物車'}
                </Option>
              </Select>
            </div>
          </div>

          {/* Session 列表：flex: 1 + minHeight: 0 讓列表佔滿剩餘高度並可捲動 */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin />
              </div>
            ) : sessions.length === 0 ? (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  padding: 16
                }}
              >
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('chatBotInbox.empty') || '暫無對話紀錄'}
                />
                <Button size="small" icon={<ReloadOutlined />} onClick={loadSessions}>
                  {t('workflowMonitor.refresh') || '重新整理'}
                </Button>
              </div>
            ) : (
              <ChatSessionList
                sessions={sessions}
                activeSessionId={activeSessionId}
                interventionMessages={interventionMessages}
                unreadCounts={unreadCounts}
                newSessionIds={newSessionIds}
                onSelect={setActiveSessionId}
                onRefresh={loadSessions}
                width="100%"
                getExtraInfo={getExtraInfo}
                isPolling
              />
            )}
          </div>
        </div>

        {/* 右欄 */}
        {activeSession ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
            {/* 對話 header */}
            <div
              style={{
                padding: '10px 16px',
                borderBottom: '1px solid #f0f0f0',
                background: '#fff',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}
            >
              <Avatar
                size={36}
                src={activeSession.contactAvatarUrl || undefined}
                style={{ backgroundColor: '#25d366', flexShrink: 0 }}
                icon={!activeSession.contactAvatarUrl ? <UserOutlined /> : undefined}
              >
                {!activeSession.contactAvatarUrl
                  ? getInitial(activeSession.contactName || activeSession.waId)
                  : null}
              </Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text strong style={{ display: 'block' }}>
                  {activeSession.contactName
                    ? `${activeSession.contactName} (${activeSession.waId})`
                    : activeSession.waId}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                  {activeSession.aiStaffName && (
                    <>
                      <RobotOutlined style={{ marginRight: 4 }} />
                      {activeSession.aiStaffName}
                    </>
                  )}
                  {activeSession.workflowName && (
                    <span style={{ marginInlineStart: activeSession.aiStaffName ? 8 : 0 }}>
                      {activeSession.workflowName}
                      {activeSession.taskName && ` · ${activeSession.taskName}`}
                    </span>
                  )}
                  {activeSession.updatedAt && (
                    <span style={{ marginInlineStart: 8 }}>
                      · {formatDateTime(activeSession.updatedAt)}
                    </span>
                  )}
                </Text>
              </div>
            </div>

            {/* 用戶正在輸入時的「新訊息等待重載」提示 */}
            {newMessagesForActive && (
              <Alert
                type="warning"
                icon={<BellOutlined />}
                showIcon
                banner
                message={
                  <span>
                    {t('chatBotInbox.newMessagesAvailable') || '有新訊息'}
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: '0 4px' }}
                      onClick={loadSessions}
                    >
                      {t('chatBotInbox.reloadNow') || '立即載入'}
                    </Button>
                  </span>
                }
                style={{ borderRadius: 0, flexShrink: 0 }}
              />
            )}

            <ChatConversationPanel
              messages={combinedMessages}
              activeSession={activeSession}
              inputText={inputText}
              onInputChange={setInputText}
              onSend={handleSendIntervention}
              sending={sending}
              visible
              adminHintFallback={
                t('workflowMonitor.chatBotAdminInterventionHint') ||
                '此處發送的訊息為 admin 介入，不會寫入 ChatBot 對話歷史'
              }
            />
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('chatBotInbox.selectSession') || '請從左側選擇一個對話'}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatBotInboxPage;
